import { createContext, useContext, ReactNode, useMemo } from 'react'
import type { IDataProvider } from '@/core/interfaces'
import { DataProviderFactory, type ProviderType } from '@/core/factories'

const DataProviderContext = createContext<IDataProvider | null>(null)

interface DataProviderProviderProps {
  children: ReactNode
  provider?: IDataProvider
  providerType?: ProviderType
}

/**
 * Provider component that supplies the data provider to the application
 * Can accept either a provider instance or a provider type
 */
export function DataProviderProvider({
  children,
  provider,
  providerType,
}: DataProviderProviderProps) {
  const value = useMemo(() => {
    // If a provider instance is passed, use it
    if (provider) {
      return provider
    }

    // If a provider type is passed, create it using the factory
    if (providerType) {
      return DataProviderFactory.create(providerType)
    }

    // Otherwise, use the default provider
    return DataProviderFactory.createDefault()
  }, [provider, providerType])

  return <DataProviderContext.Provider value={value}>{children}</DataProviderContext.Provider>
}

/**
 * Hook to access the data provider from any component
 * @throws Error if used outside of DataProviderProvider
 */
export function useDataProvider(): IDataProvider {
  const context = useContext(DataProviderContext)
  if (!context) {
    throw new Error('useDataProvider must be used within DataProviderProvider')
  }
  return context
}
